import { 
  createNestablePublicClientApplication, 
  InteractionRequiredAuthError,
  IPublicClientApplication,
  AccountInfo 
} from "@azure/msal-browser";
import { AZURE_CLIENT_ID } from "../../config";

export interface UserIdentity {
  displayName?: string;
  email?: string;
  rawAccessToken?: string;
}

// MSAL instance cache
let msalInstance: IPublicClientApplication | undefined = undefined;

/**
 * Checks if Nested App Authentication (NAA) is supported
 */
function isNaaSupported(): boolean {
  try {
    return Office.context.requirements.isSetSupported("NestedAppAuth", "1.1");
  } catch (error) {
    console.warn("Could not check NAA support:", error);
    return false;
  }
}

/**
 * Checks if the current host is Outlook
 */
function isOutlookHost(): boolean {
  try {
    return Office.context.host === Office.HostType.Outlook;
  } catch (error) {
    return false;
  }
}

/**
 * Checks if the current host is Word, Excel, or PowerPoint
 */
function isDocumentHost(): boolean {
  try {
    const host = Office.context.host;
    return host === Office.HostType.Word || 
           host === Office.HostType.Excel || 
           host === Office.HostType.PowerPoint;
  } catch (error) {
    return false;
  }
}

/**
 * Get the login hint from Office.AuthContext for a better SSO experience.
 * This is especially important for Office in a browser (Word, Excel, PowerPoint).
 */
async function getLoginHint(): Promise<string | null> {
  try {
    if (typeof Office !== "undefined" && Office.context && Office.auth) {
      // Office.auth.getAuthContext() returns a promise that resolves to AuthContext
      const authContext = await (Office.auth as any).getAuthContext();
      if (authContext?.userPrincipalName) {
        console.log("Login hint retrieved:", authContext.userPrincipalName);
        return authContext.userPrincipalName;
      }
    }
  } catch (error) {
    console.warn("Could not get login hint:", error);
  }
  return null;
}

/**
 * Initializes MSAL as a nestable public client application for Outlook NAA
 * Should be called in Office.onReady() for best practices
 */
export async function initMsal(): Promise<IPublicClientApplication | null> {
  if (msalInstance) {
    return msalInstance;
  }

  try {
    const msalConfig = {
      auth: {
        clientId: AZURE_CLIENT_ID,
        authority: "https://login.microsoftonline.com/common",
        supportsNestedAppAuth: true
      },
      cache: {
        cacheLocation: "localStorage" as const
      },
      system: {
        iframeHashTimeout: 1000, // Applies just to silent calls - In milliseconds
      }
    };

    msalInstance = await createNestablePublicClientApplication(msalConfig);
    return msalInstance;
  } catch (error) {
    console.error("Failed to initialize MSAL:", error);
    return null;
  }
}

/**
 * Checks if we're running in a sandboxed environment (like SharePoint/Office Online)
 * where iframe-based silent authentication might not work
 */
function isSandboxedEnvironment(): boolean {
  try {
    if (typeof window !== 'undefined' && window.location) {
      const origin = window.location.origin.toLowerCase();
      // Check if running in SharePoint or Office Online
      return origin.includes('sharepoint.com') || 
             origin.includes('office.com') ||
             origin.includes('officeapps.live.com');
    }
  } catch (error) {
    // If we can't check, assume not sandboxed
    return false;
  }
  return false;
}

/**
 * Result type for token acquisition
 */
export interface TokenAcquisitionResult {
  success: boolean;
  token?: string;
  requiresPopup?: boolean;
  error?: Error;
}

/**
 * Unified function to acquire access token using MSAL NAA
 * Works for both Outlook and non-Outlook hosts with proper fallback logic
 * 
 * Strategy:
 * 1. Check for cached account -> try acquireTokenSilent (uses cache + silent refresh)
 * 2. If no account or silent fails -> try ssoSilent (if not sandboxed)
 * 3. If popup is needed, return requiresPopup: true instead of auto-triggering
 * 
 * @param scopes - The scopes for which the token is requested
 * @param loginHint - Optional login hint (email/UPN) for better SSO experience
 * @param allowAutoPopup - If false, returns requiresPopup instead of auto-triggering popup
 * @returns Promise<TokenAcquisitionResult> - Result with token or popup requirement
 */
async function acquireAccessToken(
  scopes: string[], 
  loginHint?: string | null,
  allowAutoPopup: boolean = false
): Promise<TokenAcquisitionResult> {
  await initMsal();
  
  if (!msalInstance) {
    throw new Error("MSAL instance not initialized");
  }

  // Get login hint if not provided (for non-Outlook hosts)
  const finalLoginHint = loginHint || await getLoginHint();
  
  // Check if we're in a sandboxed environment (SharePoint/Office Online)
  const isSandboxed = isSandboxedEnvironment();
  
  // Check if this is Outlook host
  const isOutlook = isOutlookHost();
  
  // Check if we have a cached account
  const accounts = msalInstance.getAllAccounts();
  const account = accounts.length > 0 ? accounts[0] : null;
  
  const baseRequest = {
    scopes: scopes,
    ...(finalLoginHint && { loginHint: finalLoginHint })
  };
  
  let authResult = null;
  
  // Step 1: For Outlook, always try acquireTokenSilent first (even without cached account)
  // For non-Outlook, try acquireTokenSilent only if we have a cached account
  if (isOutlook || account) {
    try {
      const tokenRequest = account 
        ? { ...baseRequest, account: account }
        : baseRequest;
      
      console.log(isOutlook 
        ? "Outlook: Attempting silent token acquisition (with or without cached account)..."
        : "Attempting silent token acquisition with cached account...");
      
      authResult = await msalInstance.acquireTokenSilent(tokenRequest);
      console.log("Silent token acquisition successful");
      return { success: true, token: authResult.accessToken };
    } catch (silentError: any) {
      // Check if error requires interaction or is due to sandbox restrictions
      const requiresInteraction = silentError instanceof InteractionRequiredAuthError;
      const isSandboxError = silentError?.message?.includes('sandboxed') || 
                            silentError?.message?.includes('allow-top-navigation') ||
                            silentError?.errorCode === 'monitor_window_timeout' ||
                            silentError?.name === 'BrowserConfigurationAuthError';
      
      if (requiresInteraction || isSandboxError) {
        console.log("Silent authentication failed, will try next method", {
          errorType: requiresInteraction ? 'InteractionRequired' : 'SandboxError',
          errorMessage: silentError?.message
        });
        // Will fall through to next step below
      } else {
        console.error("Silent token acquisition failed with non-interaction error:", silentError);
        return { 
          success: false, 
          error: silentError instanceof Error ? silentError : new Error(String(silentError))
        };
      }
    }
  }
  
  // Step 2: For non-Outlook apps only, try ssoSilent (if not sandboxed)
  // ssoSilent attempts silent SSO without requiring a cached account
  // Outlook should skip this step and go directly to popup
  if (!authResult && !isOutlook && !isSandboxed) {
    try {
      console.log("No cached account or silent failed, attempting ssoSilent...", 
                  finalLoginHint ? `(with login hint: ${finalLoginHint})` : "");
      authResult = await msalInstance.ssoSilent(baseRequest);
      console.log("Silent SSO successful");
      return { success: true, token: authResult.accessToken };
    } catch (ssoError: any) {
      // Check if error requires interaction
      const requiresInteraction = ssoError instanceof InteractionRequiredAuthError;
      const isSandboxError = ssoError?.message?.includes('sandboxed') || 
                            ssoError?.message?.includes('allow-top-navigation') ||
                            ssoError?.errorCode === 'monitor_window_timeout' ||
                            ssoError?.name === 'BrowserConfigurationAuthError';
      
      if (requiresInteraction || isSandboxError) {
        console.log("Silent SSO failed, will try popup", {
          errorType: requiresInteraction ? 'InteractionRequired' : 'SandboxError',
          errorMessage: ssoError?.message
        });
        // Will fall through to popup below
      } else {
        console.error("Silent SSO failed with non-interaction error:", ssoError);
        return { 
          success: false, 
          error: ssoError instanceof Error ? ssoError : new Error(String(ssoError))
        };
      }
    }
  }
  
  // Step 3: Popup authentication needed
  // If allowAutoPopup is false, return requiresPopup instead of auto-triggering
  if (!authResult) {
    if (allowAutoPopup) {
      // Auto-trigger popup (may be blocked by browser)
      if (isSandboxed) {
        console.log("Sandboxed environment detected, using popup authentication");
      } else {
        console.log("Falling back to popup authentication");
      }
      
      try {
        authResult = await msalInstance.acquireTokenPopup(baseRequest);
        console.log("Popup token acquisition successful");
        return { success: true, token: authResult.accessToken };
      } catch (popupError) {
        console.error("Popup token acquisition failed:", popupError);
        return { 
          success: false, 
          requiresPopup: true,
          error: popupError instanceof Error ? popupError : new Error(String(popupError))
        };
      }
    } else {
      // Return popup requirement instead of auto-triggering
      console.log("Popup authentication required, but auto-popup disabled. User interaction needed.");
      return { 
        success: false, 
        requiresPopup: true 
      };
    }
  }
  
  // Should never reach here, but just in case
  return { 
    success: false, 
    error: new Error("Could not acquire access token") 
  };
}

/**
 * Triggers popup authentication on user click
 * Must be called within 100ms of a user click event to avoid popup blockers
 * 
 * @param scopes - The scopes for which the token is requested
 * @param loginHint - Optional login hint (email/UPN) for better SSO experience
 * @returns Promise<string> - The acquired access token
 */
export async function triggerPopupAuthentication(
  scopes: string[], 
  loginHint?: string | null
): Promise<string> {
  // Ensure MSAL is initialized (should be fast if already initialized)
  await initMsal();
  
  if (!msalInstance) {
    throw new Error("MSAL instance not initialized");
  }

  // Get login hint if not provided (should be fast)
  // Note: acquireTokenPopup opens the popup synchronously, so this async call
  // happens before the popup window is opened, ensuring it's within the 100ms window
  const finalLoginHint = loginHint !== undefined ? loginHint : await getLoginHint();
  
  const baseRequest = {
    scopes: scopes,
    ...(finalLoginHint && { loginHint: finalLoginHint })
  };

  try {
    console.log("Triggering popup authentication from user click...");
    // acquireTokenPopup opens the popup window synchronously as part of the call
    // This ensures the popup is opened within the 100ms window required by browsers
    const authResult = await msalInstance.acquireTokenPopup(baseRequest);
    console.log("Popup token acquisition successful");
    return authResult.accessToken;
  } catch (popupError) {
    console.error("Popup token acquisition failed:", popupError);
    throw popupError;
  }
}

/**
 * Attempts to read the current user's identity from the Outlook mailbox APIs.
 */
function getMailboxIdentity(): UserIdentity | null {
  const userProfile = Office.context?.mailbox?.userProfile;

  if (!userProfile?.emailAddress) {
    return null;
  }

  return {
    displayName: userProfile.displayName || userProfile.emailAddress,
    email: userProfile.emailAddress,
  };
}

/**
 * Decodes a JWT payload without introducing extra dependencies.
 */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  const parts = token.split('.');
  if (parts.length < 2) {
    return null;
  }

  const payload = parts[1];
  const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
  const paddingNeeded = (4 - (normalized.length % 4 || 4)) % 4;
  const padded = normalized + '='.repeat(paddingNeeded);

  try {
    if (typeof window !== 'undefined' && typeof window.atob === 'function') {
      const decoded = window.atob(padded);
      return JSON.parse(decoded);
    }

    // Fallback for environments where window.atob is unavailable (e.g., tests)
    const buffer = typeof Buffer !== 'undefined' ? Buffer.from(padded, 'base64') : undefined;
    if (buffer) {
      return JSON.parse(buffer.toString('utf-8'));
    }
  } catch (error) {
    console.warn('Failed to decode JWT payload', error);
  }

  return null;
}

/**
 * Fetches an identity token via Office SSO (OfficeRuntime.auth) for non-Outlook hosts
 * This is used for Word, Excel, PowerPoint, etc.
 */
async function getSsoIdentityOfficeRuntime(): Promise<UserIdentity | null> {
  if (!OfficeRuntime?.auth?.getAccessToken) {
    console.error("Office SSO authentication not available");
    return null;
  }

  try {
    const accessToken = await OfficeRuntime.auth.getAccessToken({
      allowSignInPrompt: true,
    });

    console.log("accessToken", accessToken);

    const payload = decodeJwtPayload(accessToken) as Record<string, string> | null;
    const displayName =
      payload?.name ||
      payload?.given_name ||
      payload?.family_name ||
      payload?.preferred_username;

    const email =
      payload?.preferred_username ||
      payload?.email ||
      payload?.upn ||
      payload?.unique_name;

    if (!displayName && !email) {
      return { rawAccessToken: accessToken };
    }

    return {
      displayName,
      email,
      rawAccessToken: accessToken,
    };
  } catch (error) {
    console.error('Office SSO token retrieval failed', error);
    return null;
  }
}

/**
 * Gets SSO identity using MSAL NAA for all Office applications
 * Uses unified acquireAccessToken function for consistent behavior
 * 
 * @param loginHint - Optional login hint for Word, Excel, PowerPoint in browser
 * @param allowAutoPopup - If false, returns null when popup is needed instead of auto-triggering
 * @returns UserIdentity or null. If requiresPopup is true, the identity will have rawAccessToken: undefined
 */
async function getSsoIdentityMsal(
  loginHint?: string | null,
  allowAutoPopup: boolean = false
): Promise<UserIdentity | null> {
  try {
    // Use unified token acquisition function (don't auto-trigger popup)
    const scopes = ["openid", "profile", "email", "User.Read"];
    const result = await acquireAccessToken(scopes, loginHint, allowAutoPopup);
    
    // If popup is required but not allowed, return null to indicate popup is needed
    if (result.requiresPopup && !allowAutoPopup) {
      return null;
    }
    
    if (!result.success || !result.token) {
      return null;
    }
    
    // Decode access token to extract user identity claims
    const payload = decodeJwtPayload(result.token) as Record<string, string> | null;
    const displayName =
      payload?.name ||
      payload?.given_name ||
      payload?.family_name ||
      payload?.preferred_username;

    const email =
      payload?.preferred_username ||
      payload?.email ||
      payload?.upn ||
      payload?.unique_name;

    if (!displayName && !email) {
      return { rawAccessToken: result.token };
    }

    return {
      displayName,
      email,
      rawAccessToken: result.token,
    };
  } catch (error) {
    console.error("MSAL token acquisition failed:", error);
    return null;
  }
}

/**
 * Result type for getUserIdentity
 */
export interface GetUserIdentityResult {
  identity: UserIdentity | null;
  requiresPopup: boolean;
  loginHint?: string | null;
}

/**
 * Retrieves the current Office user's identity.
 * For all Office applications: Uses MSAL NAA (Nested App Authentication) when supported
 * Falls back to OfficeRuntime.auth.getAccessToken only if NAA is not supported
 * 
 * @param allowAutoPopup - If false, returns requiresPopup: true instead of auto-triggering popup
 * @returns GetUserIdentityResult with identity and popup requirement status
 */
export async function getUserIdentity(allowAutoPopup: boolean = false): Promise<GetUserIdentityResult> {
  const mailboxIdentity = getMailboxIdentity();
  const isOutlook = isOutlookHost();
  const isDocument = isDocumentHost();
  const naaSupported = isNaaSupported();

  // Use MSAL NAA for all Office applications if supported
  if (naaSupported) {
    // Get login hint for Word, Excel, PowerPoint (especially important in browser)
    let loginHint: string | null = null;
    if (isDocument) {
      console.log("Document host detected, getting login hint...");
      loginHint = await getLoginHint();
    }

    if (isOutlook) {
      // Outlook: Use existing logic with mailbox identity
      console.log("Using MSAL NAA for Outlook authentication");
      const ssoIdentity = await getSsoIdentityMsal(loginHint, allowAutoPopup);
      
      // If popup is required, return that status
      if (!ssoIdentity && !allowAutoPopup) {
        // Check if it's because popup is needed
        const scopes = ["openid", "profile", "email", "User.Read"];
        const result = await acquireAccessToken(scopes, loginHint, false);
        if (result.requiresPopup) {
          return { identity: mailboxIdentity, requiresPopup: true, loginHint };
        }
      }
      
      // Merge mailbox identity (email) with SSO token
      if (mailboxIdentity && ssoIdentity) {
        return {
          identity: {
            ...mailboxIdentity,
            rawAccessToken: ssoIdentity.rawAccessToken,
          },
          requiresPopup: false
        };
      }
      
      // If we have SSO identity but no mailbox identity, return SSO identity
      if (ssoIdentity) {
        return { identity: ssoIdentity, requiresPopup: false };
      }
      
      // If SSO failed but we have mailbox identity, return it (token will be null)
      return { identity: mailboxIdentity, requiresPopup: false };
    } else {
      // Non-Outlook hosts (Word, Excel, PowerPoint, etc.): Use unified MSAL function
      console.log("Using MSAL NAA for non-Outlook host authentication");
      const ssoIdentity = await getSsoIdentityMsal(loginHint, allowAutoPopup);
      
      if (ssoIdentity) {
        return { identity: ssoIdentity, requiresPopup: false };
      }
      
      // Check if popup is needed
      const scopes = ["openid", "profile", "email", "User.Read"];
      const result = await acquireAccessToken(scopes, loginHint, false);
      if (result.requiresPopup) {
        return { identity: null, requiresPopup: true, loginHint };
      }
      
      // If MSAL fails, fall back to OfficeRuntime
      console.log("MSAL NAA failed, falling back to OfficeRuntime.auth");
      const fallbackIdentity = await getSsoIdentityOfficeRuntime();
      return { identity: fallbackIdentity, requiresPopup: false };
    }
  }

  // Fallback: Use OfficeRuntime if NAA is not supported
  console.log("NAA not supported, using OfficeRuntime.auth for authentication");
  const ssoIdentity = await getSsoIdentityOfficeRuntime();
  
  // If we have mailbox identity (shouldn't happen for non-Outlook, but just in case)
  if (mailboxIdentity) {
    return {
      identity: {
        ...mailboxIdentity,
        rawAccessToken: ssoIdentity?.rawAccessToken || mailboxIdentity.rawAccessToken,
      },
      requiresPopup: false
    };
  }

  // Fallback to SSO identity
  return { identity: ssoIdentity, requiresPopup: false };
}












